import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { COLORS, TYPOGRAPHY } from "../designSystem";

const PRIMARY   = COLORS.PRIMARY;
const SECONDARY = COLORS.PRIMARY_DARK;
const DARK      = COLORS.GRADIENT_DARK;
const FONT      = TYPOGRAPHY.FONT_FAMILY_BODY;

const NavButton = ({ children, onClick }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "none",
        border: "none",
        color: hovered ? "#fff" : "rgba(255,255,255,0.75)",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
        padding: "8px 0",
        fontFamily: FONT,
        transition: "color 0.25s ease",
        borderBottom: hovered ? `2px solid ${PRIMARY}` : "2px solid transparent",
      }}
    >
      {children}
    </button>
  );
};

export default function Landing() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes bounce {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(8px); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <div style={{ fontFamily: FONT, margin: 0, padding: 0, color: "#fff" }}>

      {/* ── STICKY NAV ── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        backgroundColor: scrolled ? "rgba(10,10,10,0.98)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        boxShadow: scrolled ? `0 2px 8px rgba(0,0,0,0.1)` : "none",
        transition: "all 0.3s ease",
        padding: "0 48px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        height: "68px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", cursor: "pointer" }} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <img
            src="/assets/logo.png"
            alt="Emerald MCA"
            style={{ height: "32px", width: 'auto', filter: "brightness(0) invert(1)" }}
          />
          <div>
            <div style={{ fontWeight: "800", fontSize: "16px", letterSpacing: "-0.3px", color: "#fff", lineHeight: 1 }}>EMERALD</div>
            <div style={{ fontWeight: "600", fontSize: "11px", color: "rgba(255,255,255,0.6)", letterSpacing: "0.5px" }}>MCA</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "48px", alignItems: "center" }}>
          {[["Features", "features"], ["How It Works", "how"], ["States", "states"]].map(([label, id]) => (
            <NavButton key={id} onClick={() => scrollTo(id)}>
              {label}
            </NavButton>
          ))}
          <Link
            to="/login"
            style={{
              backgroundColor: PRIMARY,
              color: "#fff",
              padding: "10px 24px",
              borderRadius: "8px",
              textDecoration: "none",
              fontSize: "14px",
              fontWeight: "700",
              boxShadow: `0 4px 16px rgba(77, 219, 148, 0.35)`,
              transition: "all 0.25s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = `0 8px 24px rgba(77, 219, 148, 0.45)`;
              e.currentTarget.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = `0 4px 16px rgba(77, 219, 148, 0.35)`;
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Sign In
          </Link>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${DARK} 0%, ${COLORS.GRADIENT_DARK_TINT} 25%, ${SECONDARY} 50%, ${COLORS.GRADIENT_MID} 75%, ${PRIMARY} 100%)`,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        textAlign: "center", padding: "140px 24px 100px", position: "relative", overflow: "hidden",
      }}>
        <div style={{ position: "absolute", top: "-100px", right: "-100px", width: "500px", height: "500px", borderRadius: "50%", background: `rgba(77, 219, 148, 0.08)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: "-120px", left: "-80px", width: "440px", height: "440px", borderRadius: "50%", background: `rgba(77, 219, 148, 0.06)`, pointerEvents: "none" }} />

        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", backgroundColor: "rgba(77, 219, 148, 0.18)", border: `1px solid rgba(77, 219, 148, 0.35)`, borderRadius: "24px", padding: "8px 20px", fontSize: "13px", color: PRIMARY, marginBottom: "28px", fontWeight: "600", transition: "all 0.3s ease" }}>
            <span>◆</span> Enterprise CRM for MCA
          </div>
          <h1 style={{ fontSize: "clamp(42px, 7vw, 76px)", fontWeight: "800", margin: "0 0 24px", lineHeight: 1.05, letterSpacing: "-2px", maxWidth: "850px", color: "#fff" }}>
            MCA Management,<br />
            <span style={{ color: PRIMARY, textShadow: `0 0 30px rgba(77, 219, 148, 0.4)` }}>Simplified</span>
          </h1>
          <p style={{ fontSize: "19px", color: "rgba(255,255,255,0.8)", maxWidth: "600px", margin: "0 auto 56px", lineHeight: 1.8, fontWeight: "400" }}>
            The all-in-one CRM platform for MCA providers. Manage merchants, track advances, generate compliant disclosures, and streamline your entire workflow.
          </p>
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", justifyContent: "center" }}>
            <Link
              to="/login"
              style={{
                backgroundColor: PRIMARY,
                color: "#fff",
                padding: "18px 48px",
                borderRadius: "8px",
                textDecoration: "none",
                fontSize: "17px",
                fontWeight: "900",
                boxShadow: `0 12px 48px rgba(77, 219, 148, 0.5)`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                border: `2px solid ${PRIMARY}`,
                letterSpacing: '0.5px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = `0 16px 56px rgba(77, 219, 148, 0.65)`;
                e.currentTarget.style.backgroundColor = "#5eee9e";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = `0 12px 48px rgba(77, 219, 148, 0.5)`;
                e.currentTarget.style.backgroundColor = PRIMARY;
              }}
            >
              Get Started
            </Link>
            <button
              onClick={() => scrollTo("features")}
              style={{
                backgroundColor: "rgba(255,255,255,0.08)",
                color: "white",
                border: "2px solid rgba(255,255,255,0.4)",
                padding: "16px 48px",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "800",
                cursor: "pointer",
                fontFamily: FONT,
                transition: "all 0.3s ease",
                letterSpacing: '0.5px',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.6)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.08)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Learn More
            </button>
          </div>
        </div>

        <div style={{ position: "absolute", bottom: "40px", left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px", opacity: 0.3, animation: "bounce 2s infinite" }}>
          <span style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", fontWeight: "600" }}>Scroll</span>
          <div style={{ width: "1px", height: "36px", backgroundColor: "white" }} />
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={{ backgroundColor: COLORS.BG_SURFACE_TERTIARY, padding: "110px 24px", textAlign: "center" }}>
        <p style={{ color: PRIMARY, fontWeight: "700", fontSize: "12px", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "14px" }}>Why Emerald</p>
        <h2 style={{ fontSize: "40px", fontWeight: "800", color: "#0a0a0a", margin: "0 0 16px", letterSpacing: "-1px" }}>Everything to scale your MCA business</h2>
        <p style={{ color: "#777", fontSize: "16px", maxWidth: "620px", margin: "0 auto 72px", lineHeight: 1.7, fontWeight: "400" }}>
          Manage merchants, track advances, collaborate with your team, and ensure compliance — all in one platform. Built for growing MCA companies.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "28px", maxWidth: "1080px", margin: "0 auto" }}>
          {[
            { icon: "👥", title: "Merchant Management", desc: "Centralized merchant profiles with complete deal history, documents, and contact information." },
            { icon: "💼", title: "Deal Tracking", desc: "Monitor advance lifecycle from deal entry through funding and closure with real-time status." },
            { icon: "📄", title: "State Disclosures", desc: "Auto-generate compliant disclosure documents for 11 states with a single click." },
            { icon: "🤝", title: "Team Collaboration", desc: "Manage user roles and permissions. Keep your team aligned on deal progress and details." },
            { icon: "🧮", title: "Verified Calculations", desc: "APR, finance charges, and schedules calculated using industry-standard formulas." },
            { icon: "🔒", title: "Enterprise Security", desc: "Multi-tenant architecture with role-based access control and audit logs for compliance." },
          ].map(({ icon, title, desc }) => (
            <div
              key={title}
              style={{
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                padding: "32px 28px",
                textAlign: "left",
                border: `2px solid rgba(77, 219, 148, 0.18)`,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-6px)";
                e.currentTarget.style.boxShadow = "0 16px 40px rgba(77, 219, 148, 0.18)";
                e.currentTarget.style.borderColor = "rgba(77, 219, 148, 0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.06)";
                e.currentTarget.style.borderColor = "rgba(77, 219, 148, 0.18)";
              }}
            >
              <div style={{ fontSize: "32px", marginBottom: "18px", lineHeight: 1 }}>{icon}</div>
              <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#0a0a0a", margin: "0 0 12px" }}>{title}</h3>
              <p style={{ color: "#888", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" style={{ backgroundColor: COLORS.BG_SURFACE_SECONDARY, padding: "110px 24px", textAlign: "center" }}>
        <p style={{ color: PRIMARY, fontWeight: "700", fontSize: "12px", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "14px" }}>Workflow</p>
        <h2 style={{ fontSize: "38px", fontWeight: "800", color: "#0a0a0a", margin: "0 0 72px", letterSpacing: "-1px" }}>Manage deals from start to finish</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "48px", maxWidth: "800px", margin: "0 auto" }}>
          {[
            { step: "1", title: "Add Merchants", desc: "Create merchant profiles with company details, owners, and bank accounts in seconds." },
            { step: "2", title: "Create Advances", desc: "Enter deal structure, terms, and payment details. Track everything in one dashboard." },
            { step: "3", title: "Generate & Close", desc: "Auto-generate compliant disclosures, download, and mark deals as funded or closed." },
          ].map(({ step, title, desc }) => (
            <div key={step} style={{ textAlign: "center" }}>
              <div style={{ width: "60px", height: "60px", borderRadius: "50%", background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})`, color: "white", fontSize: "22px", fontWeight: "800", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", boxShadow: `0 4px 20px rgba(0,192,122,0.3)` }}>{step}</div>
              <h3 style={{ fontSize: "17px", fontWeight: "700", color: "#0a0a0a", margin: "0 0 10px" }}>{title}</h3>
              <p style={{ color: "#777", fontSize: "14px", lineHeight: 1.7, margin: 0 }}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── STATES ── */}
      <section id="states" style={{ background: `linear-gradient(135deg, ${DARK} 0%, ${COLORS.GRADIENT_DARK_TINT} 30%, ${SECONDARY} 60%, ${COLORS.GRADIENT_MID} 100%)`, padding: "110px 24px", textAlign: "center" }}>
        <p style={{ color: PRIMARY, fontWeight: "700", fontSize: "12px", letterSpacing: "3px", textTransform: "uppercase", marginBottom: "14px" }}>Coverage</p>
        <h2 style={{ fontSize: "38px", fontWeight: "800", color: "white", margin: "0 0 16px", letterSpacing: "-1px" }}>11 States Supported</h2>
        <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "16px", maxWidth: "460px", margin: "0 auto 52px", lineHeight: 1.7 }}>
          All states with active commercial financing disclosure requirements, with more added as regulations take effect.
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "center", maxWidth: "640px", margin: "0 auto" }}>
          {["New York", "California", "Florida", "Virginia", "Texas", "Connecticut", "Georgia", "Kansas", "Missouri", "Utah", "Louisiana"].map(state => (
            <span key={state} style={{ backgroundColor: "rgba(0,192,122,0.12)", border: `1px solid rgba(0,192,122,0.3)`, color: PRIMARY, padding: "9px 20px", borderRadius: "24px", fontSize: "14px", fontWeight: "600" }}>
              {state}
            </span>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ background: `linear-gradient(135deg, ${COLORS.GRADIENT_DARK_TINT} 0%, ${SECONDARY} 30%, ${COLORS.GRADIENT_MID} 60%, ${PRIMARY} 100%)`, padding: "110px 24px", textAlign: "center" }}>
        <h2 style={{ fontSize: "44px", fontWeight: "800", color: "white", margin: "0 0 16px", letterSpacing: "-1.5px" }}>Start managing your MCA business today</h2>
        <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "18px", maxWidth: "480px", margin: "0 auto 44px", lineHeight: 1.6 }}>
          Sign in to Emerald and streamline your merchant pipeline, deal tracking, and compliance workflow.
        </p>
        <Link
          to="/login"
          style={{
            backgroundColor: "#fff",
            color: SECONDARY,
            padding: "18px 56px",
            borderRadius: "8px",
            textDecoration: "none",
            fontSize: "18px",
            fontWeight: "900",
            display: "inline-block",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            border: "2px solid #fff",
            letterSpacing: '0.5px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow = "0 12px 48px rgba(0,0,0,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,0.25)";
          }}
        >
          Sign In to Get Started
        </Link>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ backgroundColor: DARK, padding: "40px 24px", textAlign: "center", borderTop: `1px solid rgba(0,192,122,0.1)` }}>
        <p style={{ color: "rgba(255,255,255,0.25)", fontSize: "13px", margin: 0, fontWeight: "500" }}>
          &copy; {new Date().getFullYear()} Finvance LLC. All rights reserved.
        </p>
      </footer>

    </div>
  );
}
